// This file is auto-generated by running bin/generate-bindings.
// DO NOT EDIT THIS FILE OR DO NOT UPDATE THIS FILE IN ANY OTHER WAY THAN BY RUNNING bin/generate-bindings.
//
// Since this file is auto-generated, any changes you make to it will be overwritten by the next run of bin/generate-bindings.

import * as z from "zod"

/**
 *
 * Indicates what is the user's completion status for a exercise.
 *
 * As close as possible to LTI's activity progress for compatibility: <https://www.imsglobal.org/spec/lti-ags/v2p0#activityprogress>.
 */
export const zActivityProgress = z.enum([
  "Initialized",
  "Started",
  "InProgress",
  "Submitted",
  "Completed",
])

/**
 * One host-stored file that an answer consists of.
 */
export const zAnswerFile = z.object({
  id: z.uuid(),
  mime: z.string(),
  name: z.string(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  size_bytes: z.coerce
    .bigint()
    .min(BigInt("-9223372036854775808"), {
      error: "Invalid value: Expected int64 to be >= -9223372036854775808",
    })
    .max(BigInt("9223372036854775807"), {
      error: "Invalid value: Expected int64 to be <= 9223372036854775807",
    })
    .nullish(),
  url: z.string(),
})

/**
 * Which of a submission's two answer representations is the answer: the opaque blob in
 * `data_json`, or the rows in `exercise_task_submission_files`.
 */
export const zAnswerKind = z.enum(["json", "file"])

export const zChapterLockingStatus = z.enum([
  "unlocked",
  "completed_and_locked",
  "not_unlocked_yet",
])

export const zChapterStatus = z.enum(["open", "closed"])

export const zChapterWithStatus = z.object({
  chapter_image_url: z.string().nullish(),
  chapter_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  color: z.string().nullish(),
  course_id: z.uuid(),
  course_module_id: z.uuid(),
  created_at: z.iso.datetime(),
  deadline: z.iso.datetime().nullish(),
  deleted_at: z.iso.datetime().nullish(),
  earliest_exercise_deadline_override: z.iso.datetime().nullish(),
  exercise_deadline_override_count: z.coerce
    .bigint()
    .min(BigInt("-9223372036854775808"), {
      error: "Invalid value: Expected int64 to be >= -9223372036854775808",
    })
    .max(BigInt("9223372036854775807"), {
      error: "Invalid value: Expected int64 to be <= 9223372036854775807",
    }),
  exercise_deadline_override_distinct_count: z.coerce
    .bigint()
    .min(BigInt("-9223372036854775808"), {
      error: "Invalid value: Expected int64 to be >= -9223372036854775808",
    })
    .max(BigInt("9223372036854775807"), {
      error: "Invalid value: Expected int64 to be <= 9223372036854775807",
    }),
  front_page_id: z.uuid().nullish(),
  id: z.uuid(),
  name: z.string(),
  opens_at: z.iso.datetime().nullish(),
  status: zChapterStatus,
  updated_at: z.iso.datetime(),
})

export const zChatbotConversation = z.object({
  anonymous_token: z.string().nullish(),
  chatbot_configuration_id: z.uuid(),
  conversation_title: z.string().nullish(),
  course_id: z.uuid().nullish(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid().nullish(),
})

export const zChatbotConversationMessageCitation = z.object({
  citation_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  content: z.string(),
  conversation_id: z.uuid(),
  conversation_message_id: z.uuid(),
  course_material_chapter_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  document_url: z.string(),
  id: z.uuid(),
  title: z.string(),
  updated_at: z.iso.datetime(),
})

export const zChatbotConversationMessageReasoning = z.object({
  chatbot_conversation_message_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  reasoning_id: z.string(),
  response_id: z.string(),
  summary: z.string().nullish(),
  updated_at: z.iso.datetime(),
})

export const zChatbotConversationSuggestedMessage = z.object({
  conversation_message_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  message: z.string(),
  updated_at: z.iso.datetime(),
})

/**
 * What the learner has open when they send a message.
 *
 * Only the page id is accepted: everything the model reads is looked up from the database.
 * The context becomes a developer message, which the model weighs above the learner's own
 * words, so a client that could write its text could give itself instructions.
 */
export const zChatbotPageContext = z.object({
  page_id: z.uuid(),
})

/**
 * The tool ran on the client. `result` is JSON of whatever shape the tool defines.
 */
export const zClientToolAnswer = z.object({
  data: z.object({
    result: z.record(z.string(), z.unknown()),
  }),
  type: z.enum(["Data"]),
})

/**
 * The name of a client tool, generated into the frontend as a string union so it names one of
 * [ClientChatbotTool::NAME] by construction instead of by a hand-copied literal.
 *
 * The bounds a tool enforces on its arguments and the shape of its answer stay hand-written on
 * the frontend: routing those through the OpenAPI schema would need either a schema per tool or
 * widening the argument and answer types this crate uses to serialize them, for a part of the
 * contract that only fails loudly, unlike the name.
 */
export const zClientToolName = z.enum([
  "ask_multiple_choice_question",
  "generate_password_reset_link",
  "reset_exercises",
  "update_cheating_status",
  "edit_user_account",
  "update_certificate",
])

export const zChatbotToolResponse = z.object({
  answer: zClientToolAnswer,
  tool_call_id: z.string(),
  tool_name: zClientToolName,
})

export const zCodeGiveawayStatus = z.union([
  z.object({
    tag: z.enum(["Disabled"]),
  }),
  z.object({
    tag: z.enum(["NotEligible"]),
  }),
  z.object({
    codes_left: z.boolean(),
    tag: z.enum(["Eligible"]),
  }),
  z.object({
    given_code: z.string(),
    tag: z.enum(["AlreadyGottenCode"]),
  }),
])

/**
 * The AI policy a teacher has selected for a course. Drives which variant of the student-facing
 * AI usage notice is shown; `NotSet` (the default) keeps the generic default message.
 */
export const zCourseAiPolicy = z.enum([
  "NotSet",
  "NoAi",
  "PlanningOnly",
  "Limited",
  "FullUse",
  "Required",
])

export const zCourseBackgroundQuestionAnswer = z.object({
  answer_value: z.string().nullish(),
  course_background_question_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

export const zCourseBackgroundQuestionType = z.enum(["Checkbox", "Text"])

export const zCourseBackgroundQuestion = z.object({
  course_id: z.uuid(),
  course_instance_id: z.uuid().nullish(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  question_text: z.string(),
  question_type: zCourseBackgroundQuestionType,
  updated_at: z.iso.datetime(),
})

export const zCourseBackgroundQuestionsAndAnswers = z.object({
  answers: z.array(zCourseBackgroundQuestionAnswer),
  background_questions: z.array(zCourseBackgroundQuestion),
})

export const zCourseCustomPrivacyPolicyCheckboxText = z.object({
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  text_html: z.string(),
  text_slug: z.string(),
  updated_at: z.iso.datetime(),
})

export const zCourseInstance = z.object({
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  description: z.string().nullish(),
  ends_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  name: z.string().nullish(),
  starts_at: z.iso.datetime().nullish(),
  support_email: z.string().nullish(),
  teacher_in_charge_email: z.string(),
  teacher_in_charge_name: z.string(),
  updated_at: z.iso.datetime(),
})

export const zCourseInstanceEnrollment = z.object({
  course_id: z.uuid(),
  course_instance_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

/**
 * All the necessary info that can be used to switch the user's browser to a different language version of the course.
 */
export const zCourseLanguageVersionNavigationInfo = z.object({
  course_id: z.uuid(),
  course_language_group_id: z.uuid(),
  course_slug: z.string(),
  current_page_unavailable_in_this_language: z.boolean(),
  is_draft: z.boolean(),
  language_code: z.string(),
  page_path: z.string(),
})

/**
 * A subset of the `Course` struct that contains the fields that are allowed to be shown to all students on the course materials.
 */
export const zCourseMaterialCourse = z.object({
  ai_policy: zCourseAiPolicy,
  ask_marketing_consent: z.boolean(),
  base_module_completion_requires_n_submodule_completions: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  chapter_locking_enabled: z.boolean(),
  closed_additional_message: z.string().nullish(),
  closed_at: z.iso.datetime().nullish(),
  closed_course_successor_id: z.uuid().nullish(),
  content_search_language: z.string().nullish(),
  copied_from: z.uuid().nullish(),
  course_language_group_id: z.uuid(),
  course_material_ai_instructions: z.boolean().nullish(),
  description: z.string().nullish(),
  id: z.uuid(),
  is_draft: z.boolean(),
  is_joinable_by_code_only: z.boolean(),
  is_test_mode: z.boolean(),
  is_unlisted: z.boolean(),
  language_code: z.string(),
  name: z.string(),
  organization_id: z.uuid(),
  slug: z.string(),
})

export const zCourseMaterialCourseModule = z.object({
  chapters: z.array(zChapterWithStatus),
  id: z.uuid(),
  is_default: z.boolean(),
  name: z.string().nullish(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
})

export const zChaptersWithStatus = z.object({
  is_previewable: z.boolean(),
  modules: z.array(zCourseMaterialCourseModule),
})

export const zCourseMaterialOEmbedResponse = z.object({
  author_name: z.string(),
  author_url: z.string(),
  html: z.string(),
  provider_name: z.string(),
  provider_url: z.string(),
  title: z.string(),
  version: z.string(),
})

/**
 * Like `PeerOrSelfReviewConfig` but only the fields it's fine to show to all users.
 */
export const zCourseMaterialPeerOrSelfReviewConfig = z.object({
  course_id: z.uuid(),
  exercise_id: z.uuid().nullish(),
  id: z.uuid(),
  peer_reviews_to_give: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  peer_reviews_to_receive: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
})

export const zCourseMaterialPeerOrSelfReviewQuestionAnswer = z.object({
  number_data: z.number().nullish(),
  peer_or_self_review_question_id: z.uuid(),
  text_data: z.string().nullish(),
})

export const zCourseMaterialPeerOrSelfReviewSubmission = z.object({
  exercise_slide_submission_id: z.uuid(),
  peer_or_self_review_config_id: z.uuid(),
  peer_review_question_answers: z.array(zCourseMaterialPeerOrSelfReviewQuestionAnswer),
  token: z.string(),
})

export const zCourseModuleCompletion = z.object({
  completion_date: z.iso.datetime(),
  completion_granter_user_id: z.uuid().nullish(),
  completion_language: z.string(),
  completion_registration_attempt_date: z.iso.datetime().nullish(),
  course_id: z.uuid(),
  course_module_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  eligible_for_ects: z.boolean(),
  email: z.string(),
  grade: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  id: z.uuid(),
  needs_to_be_reviewed: z.boolean(),
  passed: z.boolean(),
  prerequisite_modules_completed: z.boolean(),
  register_credits_via_suotar: z.boolean(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

export const zCustomViewExerciseTaskGrading = z.object({
  created_at: z.iso.datetime(),
  exercise_id: z.uuid(),
  exercise_task_id: z.uuid(),
  feedback_json: z.unknown().optional(),
  feedback_text: z.string().nullish(),
  id: z.uuid(),
})

export const zCustomViewExerciseTaskSpec = z.object({
  id: z.uuid(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  public_spec: z.unknown().optional(),
})

export const zCustomViewExerciseTaskSubmission = z.object({
  answer_kind: zAnswerKind,
  created_at: z.iso.datetime(),
  data_files: z.array(zAnswerFile).nullish(),
  data_json: z.unknown().optional(),
  exercise_slide_id: z.uuid(),
  exercise_slide_submission_id: z.uuid(),
  exercise_task_grading_id: z.uuid().nullish(),
  exercise_task_id: z.uuid(),
  id: z.uuid(),
})

export const zCustomViewExerciseTasks = z.object({
  exercise_tasks: z.array(zCustomViewExerciseTaskSpec),
  task_gradings: z.array(zCustomViewExerciseTaskGrading),
  task_submissions: z.array(zCustomViewExerciseTaskSubmission),
})

/**
 * How proof of control over [`UserDetail::email`] was obtained. `AdminAsserted` is the weakest.
 */
export const zEmailVerificationMethod = z.enum([
  "emailed_code",
  "password_reset_backfill",
  "tmc_confirmed",
  "admin_asserted",
])

export const zExamEnrollment = z.object({
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  ended_at: z.iso.datetime().nullish(),
  exam_id: z.uuid(),
  is_teacher_testing: z.boolean(),
  show_exercise_answers: z.boolean().nullish(),
  started_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

export const zExercise = z.object({
  chapter_id: z.uuid().nullish(),
  copied_from: z.uuid().nullish(),
  course_id: z.uuid().nullish(),
  created_at: z.iso.datetime(),
  deadline: z.iso.datetime().nullish(),
  deleted_at: z.iso.datetime().nullish(),
  exam_id: z.uuid().nullish(),
  exercise_language_group_id: z.uuid().nullish(),
  id: z.uuid(),
  limit_number_of_tries: z.boolean(),
  max_tries_per_slide: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  name: z.string(),
  needs_peer_review: z.boolean(),
  needs_self_review: z.boolean(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  page_id: z.uuid(),
  score_maximum: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  teacher_reviews_answer_after_locking: z.boolean(),
  updated_at: z.iso.datetime(),
  use_course_default_peer_or_self_review_config: z.boolean(),
})

/**
 * Where a user's answer to an exercise stands, as far as the student may know.
 *
 * Collapses the exercise state into what the exercise block itself tells the student, so the
 * underlying reviewing and grading stages never reach the client.
 */
export const zExercisePointsStatus = z.enum([
  "NotStarted",
  "GradingInProgress",
  "GradingFailed",
  "PeerReviewToGive",
  "SelfReviewToGive",
  "WaitingForPeerReviews",
  "WaitingForTeacherGrading",
  "NotAnswered",
  "Done",
])

/**
 * One exercise's row in a points breakdown.
 */
export const zExercisePointsBreakdown = z.object({
  attempts: z.coerce
    .bigint()
    .min(BigInt("-9223372036854775808"), {
      error: "Invalid value: Expected int64 to be >= -9223372036854775808",
    })
    .max(BigInt("9223372036854775807"), {
      error: "Invalid value: Expected int64 to be <= 9223372036854775807",
    }),
  attempts_limit: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  exercise_id: z.uuid(),
  name: z.string(),
  score_given: z.number(),
  score_maximum: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  status: zExercisePointsStatus,
})

export const zExerciseTaskSubmission = z.object({
  answer_kind: zAnswerKind,
  created_at: z.iso.datetime(),
  data_files: z.array(zAnswerFile).nullish(),
  data_json: z.unknown().optional(),
  deleted_at: z.iso.datetime().nullish(),
  exercise_slide_id: z.uuid(),
  exercise_slide_submission_id: z.uuid(),
  exercise_task_grading_id: z.uuid().nullish(),
  exercise_task_id: z.uuid(),
  id: z.uuid(),
  metadata: z.unknown().optional(),
  updated_at: z.iso.datetime(),
})

export const zFeedbackBlock = z.object({
  id: z.uuid(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  text: z.string().nullish(),
})

/**
 *
 *
 * Tells what's the status of the grading progress for a user and exercise.
 *
 * As close as possible LTI's grading progress for compatibility: <https://www.imsglobal.org/spec/lti-ags/v2p0#gradingprogress>
 */
export const zGradingProgress = z.enum([
  "Failed",
  "NotReady",
  "PendingManual",
  "Pending",
  "FullyGraded",
])

export const zExerciseTaskGrading = z.object({
  course_id: z.uuid().nullish(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  exam_id: z.uuid().nullish(),
  exercise_id: z.uuid(),
  exercise_task_id: z.uuid(),
  exercise_task_submission_id: z.uuid(),
  feedback_json: z.unknown().optional(),
  feedback_text: z.string().nullish(),
  grading_completed_at: z.iso.datetime().nullish(),
  grading_priority: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  grading_progress: zGradingProgress,
  grading_started_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  score_given: z.number().nullish(),
  unscaled_score_given: z.number().nullish(),
  unscaled_score_maximum: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  updated_at: z.iso.datetime(),
})

/**
 * Information necessary for the frontend to render an exercise task
 */
export const zCourseMaterialExerciseTask = z.object({
  assignment: z.unknown(),
  deleted_at: z.iso.datetime().nullish(),
  exercise_iframe_url: z.string().nullish(),
  exercise_service_slug: z.string(),
  exercise_slide_id: z.uuid(),
  id: z.uuid(),
  model_solution_spec: z.unknown().optional(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  previous_submission: zExerciseTaskSubmission.nullish(),
  previous_submission_grading: zExerciseTaskGrading.nullish(),
  pseudonumous_user_id: z.uuid().nullish(),
  public_spec: z.unknown().optional(),
})

export const zCourseMaterialExerciseSlide = z.object({
  exercise_tasks: z.array(zCourseMaterialExerciseTask),
  id: z.uuid(),
})

export const zCourseMaterialPeerOrSelfReviewDataAnswerToReview = z.object({
  course_material_exercise_tasks: z.array(zCourseMaterialExerciseTask),
  exercise_slide_submission_id: z.uuid(),
})

export const zIsChapterFrontPage = z.object({
  is_chapter_front_page: z.boolean(),
})

export const zIsTeacherTesting = z.object({
  is_teacher_testing: z.boolean(),
})

export const zLockChapterContentState = z.enum(["not_locked", "waiting_teacher_review", "visible"])

export const zMaterialReference = z.object({
  citation_key: z.string(),
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  reference: z.string(),
  updated_at: z.iso.datetime(),
})

export const zMessageRole = z.enum(["assistant", "user", "developer", "system"])

export const zChatbotConversationMessageMessage = z.object({
  chatbot_conversation_message_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  message_is_complete: z.boolean(),
  message_role: zMessageRole,
  response_id: z.string().nullish(),
  text: z.string(),
  updated_at: z.iso.datetime(),
  used_tokens: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
})

export const zNewCourseBackgroundQuestionAnswer = z.object({
  answer_value: z.string().nullish(),
  course_background_question_id: z.uuid(),
})

export const zNewFeedbackCategory = z.object({
  name: z.string(),
})

export const zNewFeedback = z.object({
  category: zNewFeedbackCategory.nullish(),
  feedback_given: z.string(),
  page_id: z.uuid(),
  related_blocks: z.array(zFeedbackBlock),
  selected_text: z.string().nullish(),
})

export const zNewProposedBlockEdit = z.object({
  block_attribute: z.string(),
  block_id: z.uuid(),
  changed_text: z.string(),
  original_text: z.string(),
})

export const zNewProposedPageEdits = z.object({
  block_edits: z.array(zNewProposedBlockEdit),
  page_id: z.uuid(),
})

export const zNewResearchFormQuestionAnswer = z.object({
  research_consent: z.boolean(),
  research_form_question_id: z.uuid(),
  user_id: z.uuid(),
})

export const zOrganization = z.object({
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  description: z.string().nullish(),
  hidden: z.boolean(),
  id: z.uuid(),
  name: z.string(),
  organization_image_url: z.string().nullish(),
  slug: z.string(),
  updated_at: z.iso.datetime(),
})

export const zPage = z.object({
  chapter_id: z.uuid().nullish(),
  content: z.unknown(),
  copied_from: z.uuid().nullish(),
  course_id: z.uuid().nullish(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  exam_id: z.uuid().nullish(),
  hidden: z.boolean(),
  id: z.uuid(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  page_language_group_id: z.uuid().nullish(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  url_path: z.string(),
})

export const zPageAudioFile = z.object({
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  mime_type: z.string(),
  page_id: z.uuid(),
  path: z.string(),
})

export const zPageChapterAndCourseInformation = z.object({
  chapter_front_page_id: z.uuid().nullish(),
  chapter_front_page_url_path: z.string().nullish(),
  chapter_name: z.string().nullish(),
  chapter_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  course_name: z.string().nullish(),
  course_slug: z.string().nullish(),
  organization_slug: z.string().nullish(),
})

/**
 * A page's exercises in a points breakdown, with their subtotal.
 */
export const zPagePointsBreakdown = z.object({
  exercises: z.array(zExercisePointsBreakdown),
  page_id: z.uuid(),
  score_given: z.number(),
  score_maximum: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  title: z.string(),
  url_path: z.string(),
})

/**
 * A chapter's pages in a points breakdown, with their subtotal.
 */
export const zChapterPointsBreakdown = z.object({
  chapter_id: z.uuid(),
  chapter_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  name: z.string(),
  pages: z.array(zPagePointsBreakdown),
  score_given: z.number(),
  score_maximum: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
})

export const zPageRoutingData = z.object({
  chapter_front_page_id: z.uuid().nullish(),
  chapter_id: z.uuid(),
  chapter_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  chapter_opens_at: z.iso.datetime().nullish(),
  page_id: z.uuid(),
  title: z.string(),
  url_path: z.string(),
})

export const zPageNavigationInformation = z.object({
  chapter_front_page: zPageRoutingData.nullish(),
  next_page: zPageRoutingData.nullish(),
  previous_page: zPageRoutingData.nullish(),
})

export const zPageSearchResult = z.object({
  chapter_name: z.string().nullish(),
  content_headline: z.string().nullish(),
  id: z.uuid(),
  rank: z.number().nullish(),
  title_headline: z.string().nullish(),
  url_path: z.string(),
})

export const zPageWithExercises = zPage.and(
  z.object({
    exercises: z.array(zExercise),
  }),
)

export const zPartnersBlock = z.object({
  content: z.unknown(),
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  updated_at: z.iso.datetime(),
})

export const zPeerOrSelfReviewQuestionSubmission = z.object({
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  number_data: z.number().nullish(),
  peer_or_self_review_question_id: z.uuid(),
  peer_or_self_review_submission_id: z.uuid(),
  text_data: z.string().nullish(),
  updated_at: z.iso.datetime(),
})

export const zPeerOrSelfReviewQuestionType = z.enum(["Essay", "Scale"])

export const zPeerOrSelfReviewQuestion = z.object({
  answer_required: z.boolean(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  peer_or_self_review_config_id: z.uuid(),
  question: z.string(),
  question_type: zPeerOrSelfReviewQuestionType,
  updated_at: z.iso.datetime(),
  weight: z.number(),
})

export const zPeerOrSelfReviewSubmission = z.object({
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  exercise_id: z.uuid(),
  exercise_slide_submission_id: z.uuid(),
  id: z.uuid(),
  peer_or_self_review_config_id: z.uuid(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

export const zPeerOrSelfReviewsReceived = z.object({
  peer_or_self_review_question_submissions: z.array(zPeerOrSelfReviewQuestionSubmission),
  peer_or_self_review_questions: z.array(zPeerOrSelfReviewQuestion),
  peer_or_self_review_submissions: z.array(zPeerOrSelfReviewSubmission),
})

/**
 *
 * Determines how we will treat the answer being peer reviewed once it has received enough reviews and the student has given enough peer reviews.
 *
 * Some strategies compare the overall received peer review likert answer (1-5) average to peer_reviews.accepting threshold.
 */
export const zPeerReviewProcessingStrategy = z.enum([
  "AutomaticallyGradeByAverage",
  "AutomaticallyGradeOrManualReviewByAverage",
  "ManualReviewEverything",
])

export const zPeerOrSelfReviewConfig = z.object({
  accepting_threshold: z.number(),
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  exercise_id: z.uuid().nullish(),
  id: z.uuid(),
  manual_review_cutoff_in_days: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  peer_reviews_to_give: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  peer_reviews_to_receive: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  points_are_all_or_nothing: z.boolean(),
  processing_strategy: zPeerReviewProcessingStrategy,
  reset_answer_if_zero_points_from_review: z.boolean(),
  review_instructions: z.unknown().optional(),
  updated_at: z.iso.datetime(),
})

export const zCourseMaterialPeerOrSelfReviewData = z.object({
  answer_to_review: zCourseMaterialPeerOrSelfReviewDataAnswerToReview.nullish(),
  num_peer_reviews_given: z.coerce
    .bigint()
    .min(BigInt("-9223372036854775808"), {
      error: "Invalid value: Expected int64 to be >= -9223372036854775808",
    })
    .max(BigInt("9223372036854775807"), {
      error: "Invalid value: Expected int64 to be <= 9223372036854775807",
    }),
  peer_or_self_review_config: zPeerOrSelfReviewConfig,
  peer_or_self_review_questions: z.array(zPeerOrSelfReviewQuestion),
})

export const zCourseMaterialPeerOrSelfReviewDataWithToken = z.object({
  course_material_peer_or_self_review_data: zCourseMaterialPeerOrSelfReviewData,
  token: z.string().nullish(),
})

export const zPrivacyLink = z.object({
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  title: z.string(),
  updated_at: z.iso.datetime(),
  url: z.string(),
})

export const zReportReason = z.enum(["Spam", "HarmfulContent", "AiGenerated"])

export const zFlaggedAnswer = z.object({
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  description: z.string().nullish(),
  flagged_by: z.uuid(),
  flagged_user: z.uuid(),
  id: z.uuid(),
  reason: zReportReason,
  submission_id: z.uuid(),
  updated_at: z.iso.datetime(),
})

export const zNewFlaggedAnswerWithToken = z.object({
  description: z.string().nullish(),
  flagged_by: z.uuid().nullish(),
  flagged_user: z.uuid().nullish(),
  peer_or_self_review_config_id: z.uuid(),
  reason: zReportReason,
  submission_id: z.uuid(),
  token: z.string(),
})

export const zResearchForm = z.object({
  content: z.unknown(),
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  updated_at: z.iso.datetime(),
})

export const zResearchFormQuestion = z.object({
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  question: z.string(),
  research_consent_form_id: z.uuid(),
  updated_at: z.iso.datetime(),
})

export const zResearchFormQuestionAnswer = z.object({
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  research_consent: z.boolean(),
  research_form_question_id: z.uuid(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

/**
 *
 * Tells what stage of reviewing the user is currently in. Used for for peer review, self review, and manual review. If an exercise does not involve reviewing, the value of this stage will always be `NotStarted`.
 */
export const zReviewingStage = z.enum([
  "NotStarted",
  "PeerReview",
  "SelfReview",
  "WaitingForPeerReviews",
  "WaitingForManualGrading",
  "ReviewedAndLocked",
  "Locked",
  "NotAnsweredAndLocked",
])

export const zExerciseStatus = z.object({
  activity_progress: zActivityProgress,
  grading_progress: zGradingProgress,
  reviewing_stage: zReviewingStage,
  score_given: z.number().nullish(),
})

export const zSaveCourseSettingsPayload = z.object({
  background_question_answers: z.array(zNewCourseBackgroundQuestionAnswer),
})

export const zSearchRequest = z.object({
  query: z.string(),
})

export const zSendChatbotMessage = z.object({
  message: z.string(),
  page_context: zChatbotPageContext.nullish(),
})

export const zShowExerciseAnswers = z.object({
  show_exercise_answers: z.boolean(),
})

export const zStreamEventError = z.object({
  details: z.string().nullish(),
  message: z.string(),
})

export const zChatbotChatStreamEvent = z.union([
  z.object({
    data: z.object({
      message_id: z.uuid(),
      text: z.string(),
    }),
    type: z.enum(["Delta"]),
  }),
  z.object({
    data: z.object({
      finished: z.boolean(),
      reasoning_id: z.string(),
    }),
    type: z.enum(["Reasoning"]),
  }),
  z.object({
    data: z.object({
      arguments: z.string().nullish(),
      finished: z.boolean(),
      tool_call_id: z.string(),
      tool_name: z.string().nullish(),
    }),
    type: z.enum(["ToolCall"]),
  }),
  z.object({
    type: z.enum(["Done"]),
  }),
  z.object({
    type: z.enum(["Suspended"]),
  }),
  z.object({
    data: z.object({
      payload: z.unknown(),
      tool_call_id: z.string(),
    }),
    type: z.enum(["ActionExecuted"]),
  }),
  z.object({
    data: zStreamEventError,
    type: z.enum(["Error"]),
  }),
])

export const zStudentCountry = z.object({
  country_code: z.string(),
  course_id: z.uuid(),
  course_instance_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  user_id: z.uuid(),
})

export const zStudentExerciseTaskSubmission = z.object({
  answer_kind: zAnswerKind.nullish(),
  data_files: z.array(z.uuid()).nullish(),
  data_json: z.unknown().optional(),
  exercise_task_id: z.uuid(),
})

/**
 * Contains data sent by the student when they make a submission for an exercise slide.
 */
export const zStudentExerciseSlideSubmission = z.object({
  exercise_slide_id: z.uuid(),
  exercise_task_submissions: z.array(zStudentExerciseTaskSubmission),
})

export const zStudentExerciseTaskSubmissionResult = z.object({
  exercise_task_exercise_service_slug: z.string(),
  grading: zExerciseTaskGrading.nullish(),
  model_solution_spec: z.unknown().optional(),
  submission: zExerciseTaskSubmission,
})

export const zTeacherDecisionType = z.enum([
  "FullPoints",
  "ZeroPoints",
  "CustomPoints",
  "SuspectedPlagiarism",
  "RejectAndReset",
  "UnauthorizedAiUse",
  "BadAnswer",
  "Other",
])

/**
 * What the student is told about a teacher's grading decision.
 *
 * The decision type is included so the material can explain the decision in the student's own
 * language; the justification is whatever the teacher wrote on top of that, if anything.
 */
export const zCourseMaterialTeacherGradingDecision = z.object({
  justification: z.string().nullish(),
  teacher_decision: zTeacherDecisionType,
})

export const zExamEnrollmentData = z.union([
  z.object({
    enrollment: zExamEnrollment,
    page: zPage,
    page_id: z.uuid(),
    tag: z.enum(["EnrolledAndStarted"]),
  }),
  z.object({
    can_enroll: z.boolean(),
    tag: z.enum(["NotEnrolled"]),
  }),
  z.object({
    tag: z.enum(["NotYetStarted"]),
  }),
  z.object({
    tag: z.enum(["StudentTimeUp"]),
  }),
  z.object({
    enrollment: zExamEnrollment,
    gradings: z.array(
      z.tuple([
        z.object({
          created_at: z.iso.datetime(),
          deleted_at: z.iso.datetime().nullish(),
          hidden: z.boolean().nullish(),
          id: z.uuid(),
          justification: z.string().nullish(),
          score_given: z.number(),
          teacher_decision: zTeacherDecisionType,
          updated_at: z.iso.datetime(),
          user_exercise_state_id: z.uuid(),
          user_id: z.uuid().nullish(),
        }),
        z.object({
          chapter_id: z.uuid().nullish(),
          copied_from: z.uuid().nullish(),
          course_id: z.uuid().nullish(),
          created_at: z.iso.datetime(),
          deadline: z.iso.datetime().nullish(),
          deleted_at: z.iso.datetime().nullish(),
          exam_id: z.uuid().nullish(),
          exercise_language_group_id: z.uuid().nullish(),
          id: z.uuid(),
          limit_number_of_tries: z.boolean(),
          max_tries_per_slide: z
            .int()
            .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
            .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
            .nullish(),
          name: z.string(),
          needs_peer_review: z.boolean(),
          needs_self_review: z.boolean(),
          order_number: z
            .int()
            .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
            .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
          page_id: z.uuid(),
          score_maximum: z
            .int()
            .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
            .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
          teacher_reviews_answer_after_locking: z.boolean(),
          updated_at: z.iso.datetime(),
          use_course_default_peer_or_self_review_config: z.boolean(),
        }),
      ]),
    ),
    tag: z.enum(["StudentCanViewGrading"]),
  }),
])

export const zExamData = z.object({
  ended: z.boolean(),
  ends_at: z.iso.datetime(),
  enrollment_data: zExamEnrollmentData,
  id: z.uuid(),
  instructions: z.unknown(),
  language: z.string(),
  name: z.string(),
  starts_at: z.iso.datetime(),
  time_minutes: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
})

export const zTerm = z.object({
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  definition: z.string(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  term: z.string(),
  updated_at: z.iso.datetime(),
})

export const zTermUpdate = z.object({
  definition: z.string(),
  term: z.string(),
})

/**
 * Who answers a tool call, which decides what happens to a call that has no output yet.
 */
export const zToolKind = z.enum(["function", "azure_ai_search", "client_tool"])

export const zChatbotConversationMessageToolCall = z.object({
  chatbot_conversation_message_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  response_id: z.string(),
  tool_arguments: z.string(),
  tool_call_id: z.string(),
  tool_kind: zToolKind,
  tool_name: z.string(),
  updated_at: z.iso.datetime(),
})

export const zChatbotConversationMessageToolOutput = z.object({
  chatbot_conversation_message_id: z.uuid(),
  client_answer: z.record(z.string(), z.unknown()).nullish(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  output: z.string(),
  response_id: z.string(),
  tool_call_id: z.string(),
  tool_kind: zToolKind,
  updated_at: z.iso.datetime(),
})

export const zMessage = z.union([
  zChatbotConversationMessageMessage,
  zChatbotConversationMessageToolCall,
  zChatbotConversationMessageToolOutput,
  zChatbotConversationMessageReasoning,
])

export const zChatbotConversationMessage = z.object({
  conversation_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  message: zMessage,
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  updated_at: z.iso.datetime(),
})

/**
 * Everything needed to display the chatbot to the user.
 */
export const zChatbotConversationInfo = z.object({
  chatbot_name: z.string(),
  course_name: z.string().nullish(),
  current_conversation: zChatbotConversation.nullish(),
  current_conversation_message_citations: z.array(zChatbotConversationMessageCitation).nullish(),
  current_conversation_messages: z.array(zChatbotConversationMessage).nullish(),
  hide_citations: z.boolean(),
  suggested_messages: z.array(zChatbotConversationSuggestedMessage).nullish(),
})

export const zUnreturnedExercise = z.object({
  id: z.uuid(),
  name: z.string(),
})

export const zChapterLockPreview = z.object({
  has_unreturned_exercises: z.boolean(),
  unreturned_exercises: z.array(zUnreturnedExercise),
  unreturned_exercises_count: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
})

export const zUserChapterLockingStatus = z.object({
  chapter_id: z.uuid(),
  course_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  id: z.uuid(),
  status: zChapterLockingStatus,
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

export const zUserCourseChapterExerciseProgress = z.object({
  exercise_id: z.uuid(),
  score_given: z.number(),
})

export const zUserCourseExerciseServiceVariable = z.object({
  course_id: z.uuid().nullish(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  exam_id: z.uuid().nullish(),
  exercise_service_slug: z.string(),
  id: z.uuid(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
  variable_key: z.string(),
  variable_value: z.unknown(),
})

export const zCustomViewExerciseSubmissions = z.object({
  exercise_tasks: zCustomViewExerciseTasks,
  exercises: z.array(zExercise),
  user_variables: z.array(zUserCourseExerciseServiceVariable),
})

export const zStudentExerciseSlideSubmissionResult = z.object({
  exercise_status: zExerciseStatus.nullish(),
  exercise_task_submission_results: z.array(zStudentExerciseTaskSubmissionResult),
  user_course_instance_exercise_service_variables: z.array(zUserCourseExerciseServiceVariable),
})

export const zUserCourseInstanceChapterProgress = z.object({
  attempted_exercises: z
    .int()
    .gte(0)
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  score_given: z.number(),
  score_maximum: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  total_exercises: z
    .int()
    .gte(0)
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
})

export const zUserCourseProgress = z.object({
  attempted_exercises: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  attempted_exercises_required: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  automatic_completion: z.boolean(),
  course_module_id: z.uuid(),
  course_module_name: z.string(),
  course_module_order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  requires_exam: z.boolean(),
  score_given: z.number(),
  score_maximum: z
    .int()
    .gte(0)
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  score_required: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  total_exercises: z
    .int()
    .gte(0)
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
})

export const zUserCourseSettings = z.object({
  course_language_group_id: z.uuid(),
  created_at: z.iso.datetime(),
  current_course_id: z.uuid(),
  current_course_instance_id: z.uuid(),
  deleted_at: z.iso.datetime().nullish(),
  hidden: z.boolean(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

export const zCoursePageWithUserData = z.object({
  course: zCourseMaterialCourse.nullish(),
  instance: zCourseInstance.nullish(),
  is_test_mode: z.boolean(),
  lock_chapter_content_state: zLockChapterContentState.nullish(),
  organization: zOrganization.nullish(),
  page: zPage,
  settings: zUserCourseSettings.nullish(),
  was_redirected: z.boolean(),
})

export const zUserDetail = z.object({
  country: z.string().nullish(),
  created_at: z.iso.datetime(),
  email: z.string(),
  email_communication_consent: z.boolean().nullish(),
  email_verified_at: z.iso.datetime().nullish(),
  email_verified_method: zEmailVerificationMethod.nullish(),
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
})

export const zUserInfoPayload = z.object({
  country: z.string(),
  email: z.string(),
  email_communication_consent: z.boolean(),
  first_name: z.string(),
  last_name: z.string(),
})

export const zUserMarketingConsent = z.object({
  consent: z.boolean(),
  course_id: z.uuid(),
  course_language_group_id: z.uuid(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  email_subscription_in_mailchimp: z.string().nullish(),
  id: z.uuid(),
  synced_to_mailchimp_at: z.iso.datetime().nullish(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
  user_mailchimp_id: z.string().nullish(),
})

export const zUserMarketingConsentPayload = z.object({
  course_language_groups_id: z.uuid(),
  email_subscription: z.boolean(),
  marketing_consent: z.boolean(),
})

export const zUserModuleCompletionStatus = z.object({
  certificate_configuration_id: z.uuid().nullish(),
  certification_enabled: z.boolean(),
  completed: z.boolean(),
  default: z.boolean(),
  grade: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  module_id: z.uuid(),
  name: z.string(),
  order_number: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
  passed: z.boolean().nullish(),
  prerequisite_modules_completed: z.boolean(),
  register_credits_via_suotar: z.boolean(),
  registers_credits: z.boolean(),
})

export const zUserPointsUpdateStrategy = z.enum([
  "CanAddPointsButCannotRemovePoints",
  "CanAddPointsAndCanRemovePoints",
])

export const zExerciseSlideSubmission = z.object({
  course_id: z.uuid().nullish(),
  created_at: z.iso.datetime(),
  deleted_at: z.iso.datetime().nullish(),
  exam_id: z.uuid().nullish(),
  exercise_id: z.uuid(),
  exercise_slide_id: z.uuid(),
  flag_count: z
    .int()
    .min(-2147483648, { error: "Invalid value: Expected int32 to be >= -2147483648" })
    .max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" })
    .nullish(),
  id: z.uuid(),
  updated_at: z.iso.datetime(),
  user_id: z.uuid(),
  user_points_update_strategy: zUserPointsUpdateStrategy,
})

export const zCourseMaterialExercise = z.object({
  can_post_submission: z.boolean(),
  current_exercise_slide: zCourseMaterialExerciseSlide,
  exercise: zExercise,
  exercise_slide_submission_counts: z.record(
    z.string(),
    z.coerce
      .bigint()
      .min(BigInt("-9223372036854775808"), {
        error: "Invalid value: Expected int64 to be >= -9223372036854775808",
      })
      .max(BigInt("9223372036854775807"), {
        error: "Invalid value: Expected int64 to be <= 9223372036854775807",
      }),
  ),
  exercise_status: zExerciseStatus.nullish(),
  peer_or_self_review_config: zCourseMaterialPeerOrSelfReviewConfig.nullish(),
  previous_exercise_slide_submission: zExerciseSlideSubmission.nullish(),
  should_show_reset_message: z.string().nullish(),
  teacher_grading_decision: zCourseMaterialTeacherGradingDecision.nullish(),
  user_course_instance_exercise_service_variables: z.array(zUserCourseExerciseServiceVariable),
})

export const zDeleteCourseMaterialGlossaryTermPath = z.object({
  id: z.uuid(),
})

export const zUpdateCourseMaterialGlossaryTermBody = zTermUpdate

export const zUpdateCourseMaterialGlossaryTermPath = z.object({
  id: z.uuid(),
})

export const zGetCourseMaterialChapterPagesWithExercisesPath = z.object({
  chapter_id: z.uuid(),
})

/**
 * Chapter pages with exercises
 */
export const zGetCourseMaterialChapterPagesWithExercisesResponse = z.array(zPageWithExercises)

export const zLockCourseMaterialChapterPath = z.object({
  chapter_id: z.uuid(),
})

/**
 * Updated chapter locking status
 */
export const zLockCourseMaterialChapterResponse = zUserChapterLockingStatus

export const zGetCourseMaterialChapterLockPreviewPath = z.object({
  chapter_id: z.uuid(),
})

/**
 * Chapter lock preview
 */
export const zGetCourseMaterialChapterLockPreviewResponse = zChapterLockPreview

export const zGetCourseMaterialChapterPagesPath = z.object({
  chapter_id: z.uuid(),
})

/**
 * Public chapter pages
 */
export const zGetCourseMaterialChapterPagesResponse = z.array(zPage)

export const zGetCourseMaterialChapterPagesExcludingFrontPagePath = z.object({
  chapter_id: z.uuid(),
})

/**
 * Visible chapter pages without main front page
 */
export const zGetCourseMaterialChapterPagesExcludingFrontPageResponse = z.array(zPage)

/**
 * All chatbot conversations for user
 */
export const zAllUserConversationsResponse = z.array(zChatbotConversation)

export const zGetDefaultChatbotConfigurationForCoursePath = z.object({
  course_id: z.uuid(),
})

/**
 * Default chatbot configuration id
 */
export const zGetDefaultChatbotConfigurationForCourseResponse = z.uuid().nullable()

export const zGetConversationInfoPath = z.object({
  chatbot_configuration_id: z.uuid(),
})

export const zGetConversationInfoQuery = z.object({
  conversation_id: z.uuid().optional(),
})

/**
 * Selected chatbot conversation info
 */
export const zGetConversationInfoResponse = zChatbotConversationInfo

export const zGetCurrentConversationIdPath = z.object({
  chatbot_configuration_id: z.uuid(),
})

/**
 * Current conversation ID
 */
export const zGetCurrentConversationIdResponse = z.uuid().nullable()

export const zNewChatbotConversationPath = z.object({
  chatbot_configuration_id: z.uuid(),
})

/**
 * Created chatbot conversation
 */
export const zNewChatbotConversationResponse = zChatbotConversation

export const zSendChatbotMessageBody = zSendChatbotMessage

export const zSendChatbotMessagePath = z.object({
  chatbot_configuration_id: z.uuid(),
  conversation_id: z.uuid(),
})

/**
 * Chatbot response stream
 */
export const zSendChatbotMessageResponse = zChatbotChatStreamEvent

export const zSendChatbotToolResponseBody = zChatbotToolResponse

export const zSendChatbotToolResponsePath = z.object({
  chatbot_configuration_id: z.uuid(),
  conversation_id: z.uuid(),
})

/**
 * Chatbot response stream
 */
export const zSendChatbotToolResponseResponse = zChatbotChatStreamEvent

export const zUpdateTitleBody = z.string()

export const zUpdateTitlePath = z.object({
  chatbot_configuration_id: z.uuid(),
  conversation_id: z.uuid(),
})

export const zClaimCodeFromCodeGiveawayPath = z.object({
  id: z.uuid(),
})

/**
 * Claimed code
 */
export const zClaimCodeFromCodeGiveawayResponse = z.string()

export const zGetCodeGiveawayStatusPath = z.object({
  id: z.uuid(),
})

/**
 * Code giveaway status
 */
export const zGetCodeGiveawayStatusResponse = zCodeGiveawayStatus

export const zGetCourseMaterialBackgroundQuestionsAndAnswersPath = z.object({
  course_instance_id: z.uuid(),
})

/**
 * Background questions and answers
 */
export const zGetCourseMaterialBackgroundQuestionsAndAnswersResponse =
  zCourseBackgroundQuestionsAndAnswers

export const zGetCourseMaterialChapterExerciseProgressPath = z.object({
  course_instance_id: z.uuid(),
  chapter_id: z.uuid(),
})

/**
 * Course instance chapter exercise progress
 */
export const zGetCourseMaterialChapterExerciseProgressResponse = z.array(
  zUserCourseChapterExerciseProgress,
)

export const zGetCourseMaterialChapterProgressPath = z.object({
  course_instance_id: z.uuid(),
  chapter_id: z.uuid(),
})

/**
 * Course instance chapter progress
 */
export const zGetCourseMaterialChapterProgressResponse = zUserCourseInstanceChapterProgress

export const zGetCourseMaterialCourseModuleCompletionsForUserPath = z.object({
  course_instance_id: z.uuid(),
  user_id: z.uuid(),
})

/**
 * Course module completions for user
 */
export const zGetCourseMaterialCourseModuleCompletionsForUserResponse =
  z.array(zCourseModuleCompletion)

export const zGetCourseMaterialCourseModulePointsBreakdownPath = z.object({
  course_instance_id: z.uuid(),
  course_module_id: z.uuid(),
})

/**
 * The user's points by chapter, page and exercise
 */
export const zGetCourseMaterialCourseModulePointsBreakdownResponse =
  z.array(zChapterPointsBreakdown)

export const zGetCourseMaterialUserModuleCompletionsPath = z.object({
  course_instance_id: z.uuid(),
})

/**
 * User module completion statuses
 */
export const zGetCourseMaterialUserModuleCompletionsResponse = z.array(zUserModuleCompletionStatus)

export const zGetCourseMaterialUserCourseProgressPath = z.object({
  course_instance_id: z.uuid(),
})

/**
 * User course progress
 */
export const zGetCourseMaterialUserCourseProgressResponse = z.array(zUserCourseProgress)

export const zSaveCourseMaterialCourseSettingsBody = zSaveCourseSettingsPayload

export const zSaveCourseMaterialCourseSettingsPath = z.object({
  course_instance_id: z.uuid(),
})

/**
 * Course instance enrollment
 */
export const zSaveCourseMaterialCourseSettingsResponse = zCourseInstanceEnrollment

export const zGetCourseMaterialModuleIdByChapterIdPath = z.object({
  chapter_id: z.uuid(),
})

/**
 * Course module id
 */
export const zGetCourseMaterialModuleIdByChapterIdResponse = z.uuid()

export const zGetCourseMaterialDefaultModuleIdByCourseIdPath = z.object({
  course_id: z.uuid(),
})

/**
 * Default course module id
 */
export const zGetCourseMaterialDefaultModuleIdByCourseIdResponse = z.uuid()

export const zGetCourseMaterialExerciseTasksByModuleAndTypePath = z.object({
  course_module_id: z.uuid(),
  exercise_type: z.string(),
  course_instance_id: z.uuid(),
})

/**
 * Exercise tasks, exercises, and user variables
 */
export const zGetCourseMaterialExerciseTasksByModuleAndTypeResponse = zCustomViewExerciseSubmissions

export const zGetCourseMaterialCoursePath = z.object({
  course_id: z.uuid(),
})

/**
 * Course
 */
export const zGetCourseMaterialCourseResponse = zCourseMaterialCourse

export const zGetAiUsageNoticeAcknowledgementPath = z.object({
  course_id: z.uuid(),
})

/**
 * Whether the user has acknowledged the notice
 */
export const zGetAiUsageNoticeAcknowledgementResponse = z.boolean()

export const zAcknowledgeAiUsageNoticePath = z.object({
  course_id: z.uuid(),
})

/**
 * Acknowledgement recorded
 */
export const zAcknowledgeAiUsageNoticeResponse = z.boolean()

export const zGetCourseMaterialChaptersPath = z.object({
  course_id: z.uuid(),
})

/**
 * Course chapters grouped by module
 */
export const zGetCourseMaterialChaptersResponse = zChaptersWithStatus

export const zGetCourseMaterialCourseInstancesPath = z.object({
  course_id: z.uuid(),
})

/**
 * Course instances
 */
export const zGetCourseMaterialCourseInstancesResponse = z.array(zCourseInstance)

export const zGetCourseMaterialStudentCountriesPath = z.object({
  course_id: z.uuid(),
  course_instance_id: z.uuid(),
})

/**
 * Student country counts
 */
export const zGetCourseMaterialStudentCountriesResponse = z.record(
  z.string(),
  z.int().gte(0).max(2147483647, { error: "Invalid value: Expected int32 to be <= 2147483647" }),
)

export const zPostCourseMaterialStudentCountryPath = z.object({
  course_id: z.uuid(),
  course_instance_id: z.uuid(),
  country_code: z.string(),
})

/**
 * Student country recorded
 */
export const zPostCourseMaterialStudentCountryResponse = z.boolean()

export const zGetCurrentCourseMaterialCourseInstancePath = z.object({
  course_id: z.uuid(),
})

/**
 * Current course instance
 */
export const zGetCurrentCourseMaterialCourseInstanceResponse = zCourseInstance.nullable()

export const zGetCourseMaterialCustomPrivacyPolicyCheckboxTextsPath = z.object({
  course_id: z.uuid(),
})

/**
 * Custom privacy policy checkbox texts
 */
export const zGetCourseMaterialCustomPrivacyPolicyCheckboxTextsResponse = z.array(
  zCourseCustomPrivacyPolicyCheckboxText,
)

export const zPostFeedbackBody = z.array(zNewFeedback)

export const zPostFeedbackPath = z.object({
  course_id: z.uuid(),
})

/**
 * Created feedback ids
 */
export const zPostFeedbackResponse = z.array(z.uuid())

export const zGetCourseMaterialUserMarketingConsentPath = z.object({
  course_id: z.uuid(),
})

/**
 * User marketing consent
 */
export const zGetCourseMaterialUserMarketingConsentResponse = zUserMarketingConsent.nullable()

export const zGetCourseMaterialGlossaryPath = z.object({
  course_id: z.uuid(),
})

/**
 * Course glossary
 */
export const zGetCourseMaterialGlossaryResponse = z.array(zTerm)

export const zGetCourseMaterialLanguageVersionNavigationInfosPath = z.object({
  course_id: z.uuid(),
  page_id: z.uuid(),
})

/**
 * Language version navigation info
 */
export const zGetCourseMaterialLanguageVersionNavigationInfosResponse = z.array(
  zCourseLanguageVersionNavigationInfo,
)

export const zGetCourseMaterialCoursePagesPath = z.object({
  course_id: z.uuid(),
})

/**
 * Public course pages
 */
export const zGetCourseMaterialCoursePagesResponse = z.array(zPage)

export const zGetCourseMaterialPageByCourseIdAndLanguageGroupIdPath = z.object({
  course_id: z.uuid(),
  page_language_group_id: z.uuid(),
})

/**
 * Page in requested language group
 */
export const zGetCourseMaterialPageByCourseIdAndLanguageGroupIdResponse = zPage

export const zGetCourseMaterialPartnersBlockPath = z.object({
  course_id: z.uuid(),
})

/**
 * Partners block
 */
export const zGetCourseMaterialPartnersBlockResponse = zPartnersBlock.nullable()

export const zGetCourseMaterialPrivacyLinkPath = z.object({
  course_id: z.uuid(),
})

/**
 * Privacy links
 */
export const zGetCourseMaterialPrivacyLinkResponse = z.array(zPrivacyLink)

export const zGetCourseMaterialReferencesPath = z.object({
  course_id: z.uuid(),
})

/**
 * Course references
 */
export const zGetCourseMaterialReferencesResponse = z.array(zMaterialReference)

export const zGetCourseMaterialResearchConsentFormPath = z.object({
  course_id: z.uuid(),
})

/**
 * Research consent form
 */
export const zGetCourseMaterialResearchConsentFormResponse = zResearchForm.nullable()

export const zGetCourseMaterialResearchConsentFormQuestionsPath = z.object({
  course_id: z.uuid(),
})

/**
 * Research consent form questions
 */
export const zGetCourseMaterialResearchConsentFormQuestionsResponse = z.array(zResearchFormQuestion)

export const zPostCourseMaterialResearchConsentFormAnswerBody = zNewResearchFormQuestionAnswer

export const zPostCourseMaterialResearchConsentFormAnswerPath = z.object({
  course_id: z.uuid(),
})

/**
 * Research consent answer id
 */
export const zPostCourseMaterialResearchConsentFormAnswerResponse = z.uuid()

export const zGetCourseMaterialResearchConsentFormAnswersPath = z.object({
  course_id: z.uuid(),
})

/**
 * Research consent answers
 */
export const zGetCourseMaterialResearchConsentFormAnswersResponse = z.array(
  zResearchFormQuestionAnswer,
)

export const zSearchPagesWithPhraseBody = zSearchRequest

export const zSearchPagesWithPhrasePath = z.object({
  course_id: z.uuid(),
})

/**
 * Matching pages
 */
export const zSearchPagesWithPhraseResponse = z.array(zPageSearchResult)

export const zSearchPagesWithWordsBody = zSearchRequest

export const zSearchPagesWithWordsPath = z.object({
  course_id: z.uuid(),
})

/**
 * Matching pages
 */
export const zSearchPagesWithWordsResponse = z.array(zPageSearchResult)

export const zGetCourseMaterialTopLevelPagesPath = z.object({
  course_id: z.uuid(),
})

/**
 * Top-level pages
 */
export const zGetCourseMaterialTopLevelPagesResponse = z.array(zPage)

export const zGetCourseMaterialUserChapterLocksPath = z.object({
  course_id: z.uuid(),
})

/**
 * User chapter locking statuses
 */
export const zGetCourseMaterialUserChapterLocksResponse = z.array(zUserChapterLockingStatus)

export const zUpdateMarketingConsentBody = zUserMarketingConsentPayload

export const zUpdateMarketingConsentPath = z.object({
  course_id: z.uuid(),
})

/**
 * Marketing consent id
 */
export const zUpdateMarketingConsentResponse = z.uuid()

export const zGetCourseMaterialUserCourseSettingsPath = z.object({
  course_id: z.uuid(),
})

/**
 * User course settings
 */
export const zGetCourseMaterialUserCourseSettingsResponse = zUserCourseSettings.nullable()

export const zGetCourseMaterialStudentCountryPath = z.object({
  course_instance_id: z.uuid(),
})

/**
 * Selected student country
 */
export const zGetCourseMaterialStudentCountryResponse = zStudentCountry

export const zGetCourseMaterialCoursePageByPathPath = z.object({
  course_slug: z.string(),
  url_path: z.string(),
})

/**
 * Course page with user data
 */
export const zGetCourseMaterialCoursePageByPathResponse = zCoursePageWithUserData

export const zPostCourseMaterialCourseEditProposalBody = zNewProposedPageEdits

export const zPostCourseMaterialCourseEditProposalPath = z.object({
  course_slug: z.string(),
})

/**
 * Created edit proposal id
 */
export const zPostCourseMaterialCourseEditProposalResponse = z.uuid()

export const zFetchExamForTestingPath = z.object({
  id: z.uuid(),
})

/**
 * Exam data for testing
 */
export const zFetchExamForTestingResponse = zExamData

export const zResetExamProgressPath = z.object({
  id: z.uuid(),
})

export const zUpdateShowExerciseAnswersBody = zShowExerciseAnswers

export const zUpdateShowExerciseAnswersPath = z.object({
  id: z.uuid(),
})

export const zFetchExamPath = z.object({
  id: z.uuid(),
})

/**
 * Exam data
 */
export const zFetchExamResponse = zExamData

export const zEndExamTimePath = z.object({
  id: z.uuid(),
})

export const zEnrollInExamBody = zIsTeacherTesting

export const zEnrollInExamPath = z.object({
  id: z.uuid(),
})

export const zFetchExamEnrollmentPath = z.object({
  id: z.uuid(),
})

/**
 * Exam enrollment
 */
export const zFetchExamEnrollmentResponse = zExamEnrollment.nullable()

export const zGetCourseMaterialExercisePath = z.object({
  exercise_id: z.uuid(),
})

/**
 * Course material exercise
 */
export const zGetCourseMaterialExerciseResponse = zCourseMaterialExercise

export const zFetchPeerReviewDataReceivedByExerciseIdPath = z.object({
  exercise_id: z.uuid(),
  exercise_slide_submission_id: z.uuid(),
})

/**
 * Peer reviews received
 */
export const zFetchPeerReviewDataReceivedByExerciseIdResponse = zPeerOrSelfReviewsReceived

export const zPostFlagAnswerInPeerReviewBody = zNewFlaggedAnswerWithToken

export const zPostFlagAnswerInPeerReviewPath = z.object({
  exercise_id: z.uuid(),
})

/**
 * Created flagged answer
 */
export const zPostFlagAnswerInPeerReviewResponse = zFlaggedAnswer

export const zPostPeerOrSelfReviewSubmissionBody = zCourseMaterialPeerOrSelfReviewSubmission

export const zPostPeerOrSelfReviewSubmissionPath = z.object({
  exercise_id: z.uuid(),
})

/**
 * Peer or self review submitted
 */
export const zPostPeerOrSelfReviewSubmissionResponse = z.boolean()

export const zPostStartPeerOrSelfReviewPath = z.object({
  exercise_id: z.uuid(),
})

/**
 * Peer or self review started
 */
export const zPostStartPeerOrSelfReviewResponse = z.boolean()

export const zFetchPeerOrSelfReviewDataByExerciseIdPath = z.object({
  exercise_id: z.uuid(),
})

/**
 * Peer or self review data
 */
export const zFetchPeerOrSelfReviewDataByExerciseIdResponse =
  zCourseMaterialPeerOrSelfReviewDataWithToken

export const zPostSubmissionBody = zStudentExerciseSlideSubmission

export const zPostSubmissionPath = z.object({
  exercise_id: z.uuid(),
})

/**
 * Submission result
 */
export const zPostSubmissionResponse = zStudentExerciseSlideSubmissionResult

export const zGetCourseMaterialMentimeterOembedQuery = z.object({
  url: z.string(),
})

/**
 * Mentimeter oEmbed response
 */
export const zGetCourseMaterialMentimeterOembedResponse = zCourseMaterialOEmbedResponse

export const zGetCourseMaterialOrganizationPath = z.object({
  organization_id: z.uuid(),
})

/**
 * Organization
 */
export const zGetCourseMaterialOrganizationResponse = zOrganization

export const zGetCourseMaterialPageAudioFilesPath = z.object({
  page_id: z.uuid(),
})

/**
 * Page audio files
 */
export const zGetCourseMaterialPageAudioFilesResponse = z.array(zPageAudioFile)

export const zGetCourseMaterialPageByExamIdPath = z.object({
  page_id: z.uuid(),
})

/**
 * Exam page
 */
export const zGetCourseMaterialPageByExamIdResponse = zPage

export const zGetCourseMaterialPageChapterAndCourseInformationPath = z.object({
  current_page_id: z.uuid(),
})

/**
 * Page chapter and course information
 */
export const zGetCourseMaterialPageChapterAndCourseInformationResponse =
  zPageChapterAndCourseInformation

export const zGetCourseMaterialChapterFrontPagePath = z.object({
  current_page_id: z.uuid(),
})

/**
 * Chapter front page
 */
export const zGetCourseMaterialChapterFrontPageResponse = zPage.nullable()

export const zGetCourseMaterialIsPageChapterFrontPagePath = z.object({
  current_page_id: z.uuid(),
})

/**
 * Whether page is chapter front page
 */
export const zGetCourseMaterialIsPageChapterFrontPageResponse = zIsChapterFrontPage

export const zGetCourseMaterialPageNavigationPath = z.object({
  current_page_id: z.uuid(),
})

/**
 * Page navigation information
 */
export const zGetCourseMaterialPageNavigationResponse = zPageNavigationInformation

export const zGetCourseMaterialPageUrlPathPath = z.object({
  current_page_id: z.uuid(),
})

/**
 * Page URL path
 */
export const zGetCourseMaterialPageUrlPathResponse = z.string()

export const zPostCourseMaterialProposedEditsBody = zNewProposedPageEdits

export const zPostCourseMaterialProposedEditsPath = z.object({
  course_id: z.uuid(),
})

export const zUpdateCourseMaterialUserInfoBody = zUserInfoPayload

/**
 * Updated user details
 */
export const zUpdateCourseMaterialUserInfoResponse = zUserDetail

/**
 * Authenticated user details
 */
export const zGetCourseMaterialAuthenticatedUserDetailsResponse = zUserDetail

/**
 * Detected country code
 */
export const zGetCourseMaterialCountryFromIpResponse = z.string()
